import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, Route, Router, Routes } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Material UI
import { TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import BusinessIcon from '@mui/icons-material/Business';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

// Components
import ProducerCard from '../../../components/ProducerCard';
import Loading from '../../../components/global/loading/Loading';

// Hooks & Context
import { useProducer } from '../../../context/ProducerContext';
import { useDebounce } from '../../../hooks/useDebounce';
import { Producer } from '../../../models/Producer';
import AddProducer from './AddProducer';
import EditProducer from './EditProducer';

// Niestandardowy komponent przycisku
const CustomButton = ({
  to,
  onClick,
  className = '',
  variant = 'primary',
  disabled = false,
  children,
}: {
  to?: string;
  onClick?: () => void;
  className?: string;
  variant?: 'primary' | 'secondary' | 'outline';
  disabled?: boolean;
  children: React.ReactNode;
}) => {
  // Wspólne style bazowe
  const baseStyles = `
    font-medium text-sm rounded-md transition-all duration-200
    flex items-center justify-center gap-2 whitespace-nowrap
    ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
    ${className}
  `;

  // Style dla różnych wariantów
  const variantStyles = {
    primary: `bg-primary hover:bg-primary-dark text-white py-2 px-4
              shadow-sm hover:shadow hover:translate-y-[-1px] active:translate-y-[0px]`,
    secondary: `bg-gray-100 hover:bg-gray-200 text-gray-800 py-2 px-4`,
    outline: `border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 py-2 px-4`,
  };

  // Łączymy wszystkie style
  const buttonStyles = `${baseStyles} ${variantStyles[variant]}`;

  // Jeśli mamy atrybut "to", renderujemy Link, w przeciwnym razie button
  if (to) {
    return (
      <Link to={to} className={buttonStyles}>
        {children}
      </Link>
    );
  }

  return (
    <button onClick={onClick} disabled={disabled} className={buttonStyles}>
      {children}
    </button>
  );
};

function Producers() {
  const { control } = useForm();
  const [query, setQuery] = React.useState('');
  const [isLoading, setIsLoading] = useState(true);

  const [producers, setProducers] = useState<Producer[]>([]);
  const [totalProducers, setTotalProducers] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(5);
  const { getProducers } = useProducer();
  const debouncedQuery = useDebounce(query, 500);

  const getProducersData = useCallback(
    async (page: number, query: string, limit: number) => {
      setIsLoading(true);
      try {
        const response = await getProducers(page, limit, query);
        setProducers(response.producers);
        setTotalProducers(response.totalProducers);
        setTotalPages(response.totalPages);
        setCurrentPage(response.currentPage);
      } finally {
        setIsLoading(false);
      }
    },
    [getProducers],
  );

  useEffect(() => {
    getProducersData(currentPage, debouncedQuery, limit);
  }, [currentPage, debouncedQuery, limit, getProducersData]);

  return (
    <>
      <Routes>
        <Route path="dodaj" element={<AddProducer />} />
        <Route path="edytuj/:id" element={<EditProducer />} />
        <Route
          path="/"
          element={
            <div className="flex flex-col items-stretch space-y-6">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <motion.div
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.4 }}
                >
                  <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <BusinessIcon className="text-primary" />
                    Lista producentów
                  </h1>
                  <p className="text-gray-600 text-sm mt-1">
                    Zarządzaj producentami dostępnymi w sklepie
                  </p>
                </motion.div>

                <motion.div
                  initial={{ x: 20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.4 }}
                  className="self-end sm:self-auto"
                >
                  <CustomButton to="dodaj" variant="primary">
                    <AddIcon fontSize="small" />
                    <span>Dodaj producenta</span>
                  </CustomButton>
                </motion.div>
              </div>

              {/* Search and limit */}
              <motion.div
                className="grid grid-cols-1 sm:grid-cols-4 gap-4"
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div className="relative sm:col-span-3">
                  <Controller
                    name="search"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <div className="relative">
                        <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <TextField
                          {...field}
                          placeholder="Szukaj producenta (nazwa, specjalizacja, kraj)"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                          InputProps={{
                            startAdornment: <div className="w-6"></div>,
                            endAdornment: query ? (
                              <ClearIcon
                                className="cursor-pointer text-gray-400 hover:text-gray-600"
                                fontSize="small"
                                onClick={() => setQuery('')}
                              />
                            ) : null,
                          }}
                        />
                      </div>
                    )}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-700 whitespace-nowrap">
                    Wyników na stronie:
                  </label>
                  <select
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="block rounded-md border-gray-300 py-1.5 text-sm shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>
              </motion.div>

              {/* Status bar */}
              <motion.div
                className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <div>
                  Wyświetlono{' '}
                  <span className="font-semibold">{producers.length}</span> z{' '}
                  <span className="font-semibold">{totalProducers}</span>{' '}
                  producentów
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <span>
                      Strona {currentPage} z {totalPages}
                    </span>
                    <div className="flex gap-1">
                      <button
                        onClick={() =>
                          currentPage > 1 &&
                          getProducersData(currentPage - 1, query, limit)
                        }
                        disabled={currentPage <= 1}
                        className={`p-1 rounded ${
                          currentPage <= 1
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <ArrowBackIosNewIcon style={{ fontSize: '0.9rem' }} />
                      </button>
                      <button
                        onClick={() =>
                          currentPage < totalPages &&
                          getProducersData(currentPage + 1, query, limit)
                        }
                        disabled={currentPage >= totalPages}
                        className={`p-1 rounded ${
                          currentPage >= totalPages
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <ArrowForwardIosIcon style={{ fontSize: '0.9rem' }} />
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>

              {/* Content */}
              <AnimatePresence mode="wait">
                {isLoading ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    key="loading"
                    className="flex justify-center py-12"
                  >
                    <Loading />
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    key="content"
                    transition={{ duration: 0.3 }}
                  >
                    {producers.length > 0 ? (
                      <>
                        <div className="grid grid-cols-1 gap-4">
                          <AnimatePresence>
                            {producers.map((producer, index) => (
                              <motion.div
                                key={producer.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                  duration: 0.3,
                                  delay: index * 0.05,
                                }}
                              >
                                <ProducerCard
                                  id={producer.id}
                                  name={producer.name}
                                  description={producer.description!}
                                  image={producer.image!}
                                  foundedYear={producer.foundedYear}
                                  headquarters={producer.headquarters}
                                  website={producer.website}
                                  speciality={producer.speciality}
                                  products={producer.products}
                                />
                              </motion.div>
                            ))}
                          </AnimatePresence>
                        </div>

                        {/* Paginacja dla większych liczb stron */}
                        {totalPages > 1 && (
                          <div className="flex justify-center py-6">
                            <div className="flex items-center gap-1">
                              <CustomButton
                                onClick={() =>
                                  getProducersData(1, query, limit)
                                }
                                disabled={currentPage === 1}
                                variant="outline"
                                className={`px-3 py-1.5 text-xs ${
                                  currentPage === 1 ? 'opacity-50' : ''
                                }`}
                              >
                                Pierwsza
                              </CustomButton>

                              {[...Array(Math.min(5, totalPages))].map(
                                (_, idx) => {
                                  let pageNum: number;
                                  if (totalPages <= 5) {
                                    pageNum = idx + 1;
                                  } else if (currentPage <= 3) {
                                    pageNum = idx + 1;
                                  } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + idx;
                                  } else {
                                    pageNum = currentPage - 2 + idx;
                                  }

                                  return (
                                    <button
                                      key={idx}
                                      onClick={() =>
                                        getProducersData(pageNum, query, limit)
                                      }
                                      className={`w-8 h-8 flex items-center justify-center rounded-full text-sm
                              ${
                                currentPage === pageNum
                                  ? 'bg-primary text-white'
                                  : 'hover:bg-gray-100 text-gray-700'
                              }`}
                                    >
                                      {pageNum}
                                    </button>
                                  );
                                },
                              )}

                              <CustomButton
                                onClick={() =>
                                  getProducersData(totalPages, query, limit)
                                }
                                disabled={currentPage === totalPages}
                                variant="outline"
                                className={`px-3 py-1.5 text-xs ${
                                  currentPage === totalPages ? 'opacity-50' : ''
                                }`}
                              >
                                Ostatnia
                              </CustomButton>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <BusinessIcon
                          style={{ fontSize: '3rem' }}
                          className="text-gray-300 mb-2"
                        />
                        <h3 className="text-lg font-medium text-gray-700">
                          Brak producentów
                        </h3>
                        <p className="text-gray-500 mt-1">
                          Nie znaleziono żadnych producentów spełniających
                          podane kryteria
                        </p>
                        <CustomButton to="add" className="mt-4 py-2.5 px-5">
                          Dodaj pierwszego producenta
                        </CustomButton>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          }
        />
      </Routes>
    </>
  );
}

export default Producers;
